import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as BS from 'react-bootstrap';
import { convertCamelCaseKeys } from '../../../lib/objectUtils';
import * as Resource from '../../../apis/SpreadsheetReportConfigs';
import Pagination from '../../molecules/DataTableBeLike/Pagination';

const scope =
  'frontend.components.SpreadsheetReportConfigs.Modal.ImportSpreadsheetConfigModal';

const propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  onImport: PropTypes.func.isRequired,
};

const ITEMS_PER_PAGE = 30;

const ImportSpreadsheetConfigModal = ({
  show,
  onHide,
  projectId,
  onImport,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [spreadsheetReportConfigs, setSpreadsheetReportConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!show) {
      return;
    }
    setSelectedConfig(null);
    setImportError(null);
    setIsLoading(true);
    setFetchError(null);
    Resource.getImportableSpreadsheetReportConfigs(projectId)
      .then((response) => {
        if (response.error) {
          throw new Error(response.error);
        }
        const convertedCamelCaseData = convertCamelCaseKeys(
          response.data || {}
        );
        setSpreadsheetReportConfigs(
          convertedCamelCaseData.spreadsheetReportConfigs || []
        );
        setCurrentPage(1);
      })
      .catch((error) => {
        setFetchError(error.message);
        setSpreadsheetReportConfigs([]);
        setCurrentPage(1);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [show, projectId]);

  useEffect(() => {
    if (spreadsheetReportConfigs.length === 0) {
      return;
    }

    const totalPages = Math.ceil(
      spreadsheetReportConfigs.length / ITEMS_PER_PAGE
    );
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [spreadsheetReportConfigs, currentPage]);

  const handleSelectionChange = (config) => {
    setSelectedConfig(config);
    setImportError(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleImportClick = async () => {
    if (!selectedConfig) return;
    setIsImporting(true);
    setImportError(null);
    try {
      const response = await Resource.getDetails(
        selectedConfig.projectId,
        selectedConfig.spreadsheetReportConfigId
      );
      if (response.error) {
        throw new Error(response.error);
      }
      const { panels, ...restRaw } = response.data || {};
      const rest = convertCamelCaseKeys(restRaw || {});
      const normalizedPanelsItems = {};
      if (panels?.items) {
        Object.entries(panels.items).forEach(([sheetTitle, panelArray]) => {
          normalizedPanelsItems[sheetTitle] = convertCamelCaseKeys(panelArray);
        });
      }
      const normalizedPanels = {
        items: normalizedPanelsItems,
        sortedPanelKeys:
          panels?.sortedPanelKeys || panels?.sorted_panel_keys || [],
      };
      onImport({
        ...rest,
        panels: normalizedPanels,
      });
      onHide();
    } catch (error) {
      setImportError(error?.message || I18n.t(`${scope}.message.error`));
    } finally {
      setIsImporting(false);
    }
  };

  const renderTable = () => {
    if (isLoading) {
      return <p>{I18n.t('frontend.common.messages.fetching')}</p>;
    }
    if (fetchError) {
      return (
        <BS.Alert bsStyle='danger'>{I18n.t(`${scope}.message.error`)}</BS.Alert>
      );
    }
    if (spreadsheetReportConfigs.length === 0) {
      return <p>{I18n.t(`${scope}.message.empty`)}</p>;
    }

    const totalPages = Math.ceil(
      spreadsheetReportConfigs.length / ITEMS_PER_PAGE
    );
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * ITEMS_PER_PAGE;
    const paginatedConfigs = spreadsheetReportConfigs.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE
    );

    const renderPagination = () => (
      <Pagination
        totalPages={totalPages}
        currentPage={safeCurrentPage}
        left={1}
        right={1}
        window={2}
        onChange={handlePageChange}
      />
    );

    return (
      <>
        <BS.Table bordered condensed hover>
          <thead>
            <tr>
              <th>{I18n.t(`${scope}.body.table.header.select`)}</th>
              <th>{I18n.t(`${scope}.body.table.header.projectName`)}</th>
              <th>{I18n.t(`${scope}.body.table.header.configName`)}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedConfigs.map((config) => (
              <tr
                key={config.spreadsheetReportConfigId}
                role='button'
                tabIndex={0}
                style={{ cursor: 'pointer' }}
                onClick={() => handleSelectionChange(config)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleSelectionChange(config);
                  }
                }}
                aria-pressed={
                  selectedConfig?.spreadsheetReportConfigId ===
                  config.spreadsheetReportConfigId
                }
              >
                <td>
                  <input
                    type='radio'
                    name='import-spreadsheet-report-config'
                    value={config.spreadsheetReportConfigId}
                    checked={
                      selectedConfig?.spreadsheetReportConfigId ===
                      config.spreadsheetReportConfigId
                    }
                    onChange={() => handleSelectionChange(config)}
                    aria-label={I18n.t(`${scope}.body.table.header.select`)}
                    disabled={isImporting}
                  />
                </td>
                <td>{config.projectName}</td>
                <td>{config.spreadsheetReportConfigName}</td>
              </tr>
            ))}
          </tbody>
        </BS.Table>
        {totalPages > 1 && (
          <div className='text-right'>{renderPagination()}</div>
        )}
      </>
    );
  };

  return (
    <BS.Modal show={show} onHide={onHide} bsSize='large'>
      <BS.Modal.Header closeButton>
        <BS.Modal.Title>{I18n.t(`${scope}.header.title`)}</BS.Modal.Title>
      </BS.Modal.Header>

      <BS.Modal.Body>
        <p>{I18n.t(`${scope}.body.description`)}</p>
        {importError && <BS.Alert bsStyle='danger'>{importError}</BS.Alert>}
        {renderTable()}
      </BS.Modal.Body>

      <BS.Modal.Footer>
        <BS.Button
          bsStyle='primary'
          disabled={!selectedConfig || isImporting}
          onClick={handleImportClick}
        >
          {isImporting
            ? `${I18n.t(`${scope}.action.import`)}...`
            : I18n.t(`${scope}.action.import`)}
        </BS.Button>
        <BS.Button bsStyle='default' onClick={onHide}>
          {I18n.t(`${scope}.action.close`)}
        </BS.Button>
      </BS.Modal.Footer>
    </BS.Modal>
  );
};

ImportSpreadsheetConfigModal.propTypes = propTypes;

export default ImportSpreadsheetConfigModal;
